import { useState, useCallback, useMemo, useEffect, useRef, type ReactNode } from 'react'
import { useApiFetch } from '@/hooks/useApiFetch'
import { useApiMutation } from '@/hooks/useApiMutation'
import { useToast } from '@/components/ToastContainer'
import { toLocalImageUrl, apiFetch } from '@/lib/api'
import { cn } from '@/lib/cn'
import { Button, Input, Textarea, Modal, Badge, Spinner, Tooltip } from '@/ui'
import {
  ImagePlus,
  ImageOff,
  Pencil,
  Trash2,
  LayoutGrid,
  List,
  X,
  Image as ImageIcon,
  GripVertical,
} from 'lucide-react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  rectSortingStrategy,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import styles from './PromptBlocks.module.css'

// ── Types ──

interface PromptBlock {
  id: string
  slug: string
  nameKo: string
  nameEn: string
  promptText: string
  imageUrl: string | null
  thumbnailUrl: string | null
  isBuiltin: boolean
  isActive: boolean
  sortOrder: number
  weeklyUsageCount: number
  totalUsageCount: number
  popularityRank: number | null
  tags: string[]
  createdAt: string
  updatedAt: string
}

const TAG_MAX_LEN = 32
const TAG_MAX_COUNT = 20

/** 자유 입력 → 정규화: trim, 공백 압축, 빈 값 제거, 중복 제거, 길이 제한 */
function normalizeTags(input: string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of input) {
    const t = raw.trim().replace(/\s+/g, ' ').slice(0, TAG_MAX_LEN)
    if (!t || seen.has(t)) continue
    seen.add(t)
    out.push(t)
    if (out.length >= TAG_MAX_COUNT) break
  }
  return out
}

interface BlocksResponse {
  blocks: PromptBlock[]
}

interface AIModelInfo {
  id: string
  name: string
  provider: string
  enabled: boolean
  supportsReferenceImage: boolean
}

interface ModelsResponse {
  imageModels: AIModelInfo[]
}

// ── SortableItemWrap (드래그앤드롭 래퍼) ──

interface SortableItemWrapProps {
  id: string
  index: number
  isGrid: boolean
  className?: string
  children: (slots: { dragHandle: ReactNode; badge: ReactNode }) => ReactNode
}

function SortableItemWrap({ id, index, isGrid, className, children }: SortableItemWrapProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
  }
  const dragHandle = (
    <button
      type="button"
      className={styles.dragHandle}
      {...attributes}
      {...listeners}
      aria-label="드래그하여 순서 변경"
      onClick={(e) => e.stopPropagation()}
    >
      <GripVertical size={16} />
    </button>
  )
  const badge = (
    <span className={cn(styles.sortIndexBadge, isGrid ? styles.sortIndexBadgeGrid : styles.sortIndexBadgeList)}>
      #{index + 1}
    </span>
  )
  return (
    <div ref={setNodeRef} style={style} className={className}>
      {children({ dragHandle, badge })}
    </div>
  )
}

// ── BlockTrendSparkline (수정 모달 내부에서 사용) ──

interface BucketStat { bucketStart: string; usageCount: number }
interface BlockStatsResponse { blockId: string; grain: string; buckets: BucketStat[] }

function BlockTrendSparkline({ blockId }: { blockId: string | null }) {
  // grain 미지정 → 서버 기본 'week'(주간 시계열). 최근 12주 추이.
  const { data, isLoading } = useApiFetch<BlockStatsResponse>(
    blockId ? `/api/admin/prompt-blocks/${blockId}/stats?weeks=12` : null,
  )

  if (isLoading) {
    return <div className={styles.sparklineLoading}><Spinner /></div>
  }
  if (!data || data.buckets.length === 0) {
    return <div className={styles.sparklineEmpty}>아직 집계된 데이터가 없습니다</div>
  }

  // 서버가 과거 → 최신 순으로 반환하므로 그대로 그린다
  const buckets = data.buckets
  const width = 360
  const height = 80
  const padX = 6
  const padY = 8
  const maxVal = Math.max(...buckets.map((w) => w.usageCount), 1)
  const stepX = (width - padX * 2) / Math.max(buckets.length - 1, 1)

  const points = buckets.map((w, i) => ({
    x: padX + i * stepX,
    y: height - padY - (w.usageCount / maxVal) * (height - padY * 2),
    bucketStart: w.bucketStart,
    count: w.usageCount,
  }))

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')
  const areaPath = `${linePath} L${points[points.length - 1].x.toFixed(1)},${height - padY} L${points[0].x.toFixed(1)},${height - padY} Z`
  const total = buckets.reduce((sum, w) => sum + w.usageCount, 0)

  return (
    <div className={styles.sparklineWrap}>
      <div className={styles.sparklineMeta}>
        최근 {buckets.length}주 합계 {total.toLocaleString()}회 · 주간 최대 {maxVal}회
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className={styles.sparkline} preserveAspectRatio="none">
        <path d={areaPath} className={styles.sparkArea} />
        <path d={linePath} className={styles.sparkLine} />
        {points.map((p) => (
          <g key={p.bucketStart}>
            <circle cx={p.x} cy={p.y} r={2} className={styles.sparkDot} />
            <title>{`${p.bucketStart.slice(0, 10)} · ${p.count}회`}</title>
          </g>
        ))}
      </svg>
    </div>
  )
}

// ── Component ──

export function PromptBlocks() {
  const { addToast } = useToast()

  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editBlock, setEditBlock] = useState<PromptBlock | null>(null)

  // 폼 상태
  const [formSlug, setFormSlug] = useState('')
  const [formNameKo, setFormNameKo] = useState('')
  const [formNameEn, setFormNameEn] = useState('')
  const [formPromptText, setFormPromptText] = useState('')
  const [formImageUrl, setFormImageUrl] = useState<string | null>(null)
  const [imageUploading, setImageUploading] = useState(false)
  const [generatedCandidates, setGeneratedCandidates] = useState<string[]>([])
  const [imageModelId, setImageModelId] = useState('')
  const [imageGenerating, setImageGenerating] = useState(false)
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set())

  // 카드 이미지 생성 모달 상태
  const [generateTarget, setGenerateTarget] = useState<PromptBlock | null>(null)
  const [cardModelId, setCardModelId] = useState('')
  const [generatingBlockIds, setGeneratingBlockIds] = useState<Set<string>>(new Set())
  const bgAbortRefs = useRef<Map<string, AbortController>>(new Map())
  const [formSortOrder, setFormSortOrder] = useState(0)
  const [formTags, setFormTags] = useState<string[]>([])
  const [tagDraft, setTagDraft] = useState('')

  // 어드민 그리드/리스트용 다중 태그 필터 (선택된 태그 중 하나라도 포함하면 표시 — OR)
  const [filterTags, setFilterTags] = useState<string[]>([])

  // 참조 이미지 상태 (신랑/신부) — DB에서 로드
  const [refGroomUrl, setRefGroomUrl] = useState<string | null>(null)
  const [refBrideUrl, setRefBrideUrl] = useState<string | null>(null)
  const [referenceUploading, setReferenceUploading] = useState<'groom' | 'bride' | null>(null)
  const referenceImageUrls = useMemo(
    () => [refGroomUrl, refBrideUrl].filter(Boolean) as string[],
    [refGroomUrl, refBrideUrl],
  )
  const hasReferenceImages = referenceImageUrls.length > 0

  const { data: refImagesData } = useApiFetch<{ groomUrl: string | null; brideUrl: string | null }>(
    '/api/admin/prompt-blocks/reference-images',
  )
  useEffect(() => {
    if (refImagesData) {
      setRefGroomUrl(refImagesData.groomUrl)
      setRefBrideUrl(refImagesData.brideUrl)
    }
  }, [refImagesData])

  const saveReferenceImages = useCallback(
    async (groomUrl: string | null, brideUrl: string | null) => {
      try {
        await fetch('/api/admin/prompt-blocks/reference-images', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ groomUrl, brideUrl }),
        })
      } catch {
        /* 저장 실패 무시 — UI는 이미 갱신됨 */
      }
    },
    [],
  )

  // 데이터 로드
  const { data, refetch, isLoading } = useApiFetch<BlocksResponse>(
    '/api/admin/prompt-blocks?includeInactive=true',
  )

  // 이미지 모델 목록 (활성 모델만, 참조 이미지 설정 시 지원 모델만)
  const { data: modelsData } = useApiFetch<ModelsResponse>('/api/admin/ai-models')
  const imageModelOptions = useMemo(() => {
    if (!modelsData?.imageModels?.length) return []
    return modelsData.imageModels
      .filter((m) => m.enabled)
      .filter((m) => !hasReferenceImages || m.supportsReferenceImage)
      .map((m) => ({ value: m.id, label: `${m.name} (${m.provider})` }))
  }, [modelsData, hasReferenceImages])

  const { trigger: createBlock, isLoading: creating } = useApiMutation<
    unknown,
    Record<string, unknown>
  >('/api/admin/prompt-blocks', 'POST')

  const { trigger: updateBlock, isLoading: updating } = useApiMutation<
    unknown,
    Record<string, unknown>
  >('/api/admin/prompt-blocks', 'PUT')

  const { trigger: deleteBlock } = useApiMutation<unknown, never>(
    '/api/admin/prompt-blocks',
    'DELETE',
  )

  const { trigger: seedBlocks, isLoading: seeding } = useApiMutation<unknown, never>(
    '/api/admin/prompt-blocks/seed',
    'POST',
  )

  const { trigger: clearAllImages, isLoading: clearing } = useApiMutation<unknown, never>(
    '/api/admin/prompt-blocks/clear-images',
    'POST',
  )

  // 드래그앤드롭 옵티미스틱 업데이트를 위해 useState로 보유.
  // 서버 데이터(data.blocks)가 바뀌면 sortOrder 기준으로 재정렬해 반영.
  const [blocks, setBlocks] = useState<PromptBlock[]>([])

  useEffect(() => {
    if (data?.blocks) {
      setBlocks([...data.blocks].sort((a, b) => a.sortOrder - b.sortOrder))
    }
  }, [data])

  // ── 드래그앤드롭 정렬 ──
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  )

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      // 필터 활성 시 정렬을 허용하면 숨겨진 항목 순서가 꼬일 수 있어 차단
      if (filterTags.length > 0) {
        addToast({ type: 'info', message: '태그 필터를 해제한 뒤 정렬해주세요' })
        return
      }
      const { active, over } = event
      if (!over || active.id === over.id) return
      const oldIndex = blocks.findIndex((b) => b.id === active.id)
      const newIndex = blocks.findIndex((b) => b.id === over.id)
      if (oldIndex === -1 || newIndex === -1) return

      const prev = blocks
      const next = arrayMove(blocks, oldIndex, newIndex)
      setBlocks(next)

      try {
        await apiFetch('/api/admin/prompt-blocks/reorder', {
          method: 'POST',
          body: JSON.stringify({ orderedIds: next.map((b) => b.id) }),
        })
        refetch()
      } catch (err: any) {
        setBlocks(prev)
        addToast({ type: 'error', message: err?.message ?? '순서 변경에 실패했습니다' })
      }
    },
    [blocks, filterTags.length, refetch, addToast],
  )

  const resetForm = useCallback(() => {
    setFormSlug('')
    setFormNameKo('')
    setFormNameEn('')
    setFormPromptText('')
    setFormImageUrl(null)
    setGeneratedCandidates([])
    setFormSortOrder(0)
    setFormTags([])
    setTagDraft('')
  }, [])

  const openCreateModal = useCallback(() => {
    resetForm()
    setShowCreateModal(true)
  }, [resetForm])

  const openEditModal = useCallback((block: PromptBlock) => {
    setEditBlock(block)
    setFormNameKo(block.nameKo)
    setFormNameEn(block.nameEn)
    setFormPromptText(block.promptText)
    setFormImageUrl(block.imageUrl)
    setGeneratedCandidates(block.imageUrl ? [block.imageUrl] : [])
    setFormSortOrder(block.sortOrder)
    setFormTags(block.tags ?? [])
    setTagDraft('')
  }, [])

  const handleImageUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp']
      if (!allowedTypes.includes(file.type)) {
        addToast({
          type: 'error',
          message: '허용되지 않는 파일 형식입니다 (JPEG, PNG, WebP만 가능)',
        })
        return
      }
      if (file.size > 20 * 1024 * 1024) {
        addToast({ type: 'error', message: '파일 크기가 20MB를 초과합니다' })
        return
      }
      setImageUploading(true)
      try {
        // 1) Presigned URL 발급
        const presignRes = await fetch('/api/admin/presign-upload', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ contentType: file.type, fileSize: file.size }),
        })
        const presignJson = await presignRes.json()
        if (!presignJson.success) {
          addToast({
            type: 'error',
            message: presignJson.error?.message ?? 'Presigned URL 발급 실패',
          })
          return
        }
        const { uploadUrl, publicUrl } = presignJson.data
        // 2) S3 직접 업로드
        const uploadRes = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file,
        })
        if (!uploadRes.ok) {
          addToast({ type: 'error', message: 'S3 업로드 실패' })
          return
        }
        setGeneratedCandidates((prev) => [...prev, publicUrl])
        setFormImageUrl(publicUrl)
        addToast({ type: 'success', message: '이미지가 업로드되었습니다' })
      } catch {
        addToast({ type: 'error', message: '이미지 업로드 중 오류가 발생했습니다' })
      } finally {
        setImageUploading(false)
        e.target.value = ''
      }
    },
    [addToast],
  )

  const handleReferenceUpload = useCallback(
    async (role: 'groom' | 'bride', e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp']
      if (!allowedTypes.includes(file.type)) {
        addToast({
          type: 'error',
          message: '허용되지 않는 파일 형식입니다 (JPEG, PNG, WebP만 가능)',
        })
        return
      }
      if (file.size > 20 * 1024 * 1024) {
        addToast({ type: 'error', message: '파일 크기가 20MB를 초과합니다' })
        return
      }
      setReferenceUploading(role)
      try {
        const presignRes = await fetch('/api/admin/presign-upload', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ contentType: file.type, fileSize: file.size }),
        })
        const presignJson = await presignRes.json()
        if (!presignJson.success) {
          addToast({
            type: 'error',
            message: presignJson.error?.message ?? 'Presigned URL 발급 실패',
          })
          return
        }
        const { uploadUrl, publicUrl } = presignJson.data
        const uploadRes = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file,
        })
        if (!uploadRes.ok) {
          addToast({ type: 'error', message: 'S3 업로드 실패' })
          return
        }
        const newGroom = role === 'groom' ? publicUrl : refGroomUrl
        const newBride = role === 'bride' ? publicUrl : refBrideUrl
        if (role === 'groom') setRefGroomUrl(publicUrl)
        else setRefBrideUrl(publicUrl)
        await saveReferenceImages(newGroom, newBride)
        addToast({
          type: 'success',
          message: `${role === 'groom' ? '신랑' : '신부'} 참조 이미지가 설정되었습니다`,
        })
      } catch {
        addToast({ type: 'error', message: '참조 이미지 업로드 중 오류가 발생했습니다' })
      } finally {
        setReferenceUploading(null)
        e.target.value = ''
      }
    },
    [addToast, refGroomUrl, refBrideUrl, saveReferenceImages],
  )

  const handleGenerateImage = useCallback(async () => {
    const promptText = formPromptText
    if (!promptText) {
      addToast({ type: 'error', message: '프롬프트 텍스트를 먼저 입력해주세요.' })
      return
    }
    setImageGenerating(true)
    try {
      // 1) 생성 잡 등록 → jobId 즉시 수신 (API Gateway 30초 제한 회피)
      const { jobId } = await apiFetch<{ jobId: string }>('/api/admin/prompt-blocks/generate-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          promptText,
          modelId: imageModelId,
          ...(referenceImageUrls.length > 0 ? { referenceImageUrls } : {}),
        }),
      })

      // 2) 완료될 때까지 폴링 (2초 간격, 최대 5분)
      const deadline = Date.now() + 5 * 60 * 1000
      let imageUrl: string | null = null
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 2000))
        const job = await apiFetch<{
          status: 'pending' | 'processing' | 'completed' | 'partial' | 'failed' | 'cancelled'
          imageUrl: string | null
          thumbnailUrl: string | null
          errorMessage: string | null
        }>(`/api/admin/prompt-blocks/generate-image/${jobId}`)

        if (job.status === 'completed') {
          imageUrl = job.imageUrl
          break
        }
        if (job.status === 'failed' || job.status === 'cancelled') {
          throw new Error(job.errorMessage || 'AI 이미지 생성 실패')
        }
      }

      if (imageUrl) {
        setGeneratedCandidates((prev) => [...prev, imageUrl])
        setFormImageUrl(imageUrl)
        addToast({ type: 'success', message: 'AI 이미지가 생성되었습니다' })
      } else {
        addToast({ type: 'error', message: '이미지 생성 시간이 초과되었습니다. 다시 시도해주세요.' })
      }
    } catch (err) {
      addToast({ type: 'error', message: err instanceof Error ? err.message : 'AI 이미지 생성 실패' })
    } finally {
      setImageGenerating(false)
    }
  }, [formPromptText, imageModelId, referenceImageUrls, addToast])

  const handleCreate = useCallback(async () => {
    if (!formSlug || !formNameKo || !formNameEn || !formPromptText) {
      addToast({ type: 'error', message: '모든 필드를 입력해주세요.' })
      return
    }
    const res = await createBlock({
      slug: formSlug.toUpperCase().replace(/[^A-Z0-9_]/g, '_'),
      nameKo: formNameKo,
      nameEn: formNameEn,
      promptText: formPromptText,
      ...(formImageUrl ? { imageUrl: formImageUrl } : {}),
      sortOrder: formSortOrder,
      tags: formTags,
    })
    if (res) {
      addToast({ type: 'success', message: '블록이 생성되었습니다.' })
      setShowCreateModal(false)
      refetch()
    } else {
      addToast({ type: 'error', message: '블록 생성 실패' })
    }
  }, [
    formSlug,
    formNameKo,
    formNameEn,
    formPromptText,
    formImageUrl,
    formSortOrder,
    formTags,
    createBlock,
    addToast,
    refetch,
  ])

  const handleUpdate = useCallback(async () => {
    if (!editBlock) return
    const res = await updateBlock(
      {
        nameKo: formNameKo,
        nameEn: formNameEn,
        promptText: formPromptText,
        imageUrl: formImageUrl,
        sortOrder: formSortOrder,
        tags: formTags,
      },
      { pathOverride: `/api/admin/prompt-blocks/${editBlock.id}` },
    )
    if (res) {
      addToast({ type: 'success', message: '블록이 수정되었습니다.' })
      setEditBlock(null)
      refetch()
    } else {
      addToast({ type: 'error', message: '블록 수정 실패' })
    }
  }, [
    editBlock,
    formNameKo,
    formNameEn,
    formPromptText,
    formImageUrl,
    formSortOrder,
    formTags,
    updateBlock,
    addToast,
    refetch,
  ])

  const handleToggleActive = useCallback(
    async (block: PromptBlock) => {
      const res = await updateBlock(
        { isActive: !block.isActive },
        { pathOverride: `/api/admin/prompt-blocks/${block.id}` },
      )
      if (res) {
        addToast({
          type: 'success',
          message: block.isActive ? '블록이 비활성화되었습니다.' : '블록이 활성화되었습니다.',
        })
        refetch()
      }
    },
    [updateBlock, addToast, refetch],
  )

  const handleDelete = useCallback(
    async (block: PromptBlock) => {
      if (block.isBuiltin) {
        addToast({
          type: 'error',
          message: 'Builtin 블록은 삭제할 수 없습니다. 비활성화를 사용해주세요.',
        })
        return
      }
      if (!confirm(`"${block.nameKo}" 블록을 삭제하시겠습니까?`)) return
      const res = await deleteBlock(undefined, {
        pathOverride: `/api/admin/prompt-blocks/${block.id}`,
      })
      if (res !== undefined) {
        addToast({ type: 'success', message: '블록이 삭제되었습니다.' })
        refetch()
      }
    },
    [deleteBlock, addToast, refetch],
  )

  // ── 카드 이미지 생성/삭제 ──

  const openGenerateModal = useCallback((block: PromptBlock) => {
    setGenerateTarget(block)
    setCardModelId('')
  }, [])

  const startBackgroundGenerate = useCallback(
    (block: PromptBlock, modelId: string) => {
      const blockId = block.id
      setGeneratingBlockIds((prev) => new Set(prev).add(blockId))
      setGenerateTarget(null)

      const ctrl = new AbortController()
      bgAbortRefs.current.set(blockId, ctrl)

      // 비동기 잡: enqueue → 폴링 → 완료 시 블록 이미지 갱신 (API Gateway 30초 제한 회피)
      ;(async () => {
        try {
          // 1) 생성 잡 등록 → jobId 수신
          const { jobId } = await apiFetch<{ jobId: string }>('/api/admin/prompt-blocks/generate-image', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              promptText: block.promptText,
              modelId,
              ...(referenceImageUrls.length > 0 ? { referenceImageUrls } : {}),
            }),
            signal: ctrl.signal,
          })

          // 2) 완료될 때까지 폴링 (2초 간격, 최대 5분)
          const deadline = Date.now() + 5 * 60 * 1000
          let imageUrl: string | null = null
          let thumbnailUrl: string | null = null
          while (Date.now() < deadline) {
            if (ctrl.signal.aborted) return
            await new Promise((resolve) => setTimeout(resolve, 2000))
            if (ctrl.signal.aborted) return
            const job = await apiFetch<{
              status: 'pending' | 'processing' | 'completed' | 'partial' | 'failed' | 'cancelled'
              imageUrl: string | null
              thumbnailUrl: string | null
              errorMessage: string | null
            }>(`/api/admin/prompt-blocks/generate-image/${jobId}`, { signal: ctrl.signal })

            if (job.status === 'completed') {
              imageUrl = job.imageUrl ? toLocalImageUrl(job.imageUrl) : null
              thumbnailUrl = job.thumbnailUrl ? toLocalImageUrl(job.thumbnailUrl) : null
              break
            }
            if (job.status === 'failed' || job.status === 'cancelled') {
              throw new Error(job.errorMessage || '이미지 생성 실패')
            }
          }

          if (!imageUrl) {
            addToast({ type: 'error', message: `"${block.nameKo}" 이미지 생성 시간이 초과되었습니다.` })
            return
          }

          // 3) 블록에 생성 이미지 반영
          const updateRes = await updateBlock(
            {
              imageUrl,
              ...(thumbnailUrl ? { thumbnailUrl } : {}),
            },
            { pathOverride: `/api/admin/prompt-blocks/${blockId}` },
          )
          if (updateRes) {
            addToast({
              type: 'success',
              message: `"${block.nameKo}" 이미지가 생성되었습니다.`,
            })
            refetch()
          }
        } catch (e) {
          if (e instanceof DOMException && e.name === 'AbortError') return
          addToast({
            type: 'error',
            message: e instanceof Error ? e.message : `"${block.nameKo}" 이미지 생성 실패`,
          })
        } finally {
          bgAbortRefs.current.delete(blockId)
          setGeneratingBlockIds((prev) => {
            const next = new Set(prev)
            next.delete(blockId)
            return next
          })
        }
      })()
    },
    [updateBlock, addToast, refetch, referenceImageUrls],
  )

  const handleCardGenerate = useCallback(() => {
    if (!generateTarget || !cardModelId) return
    startBackgroundGenerate(generateTarget, cardModelId)
  }, [generateTarget, cardModelId, startBackgroundGenerate])

  const handleCardDeleteImage = useCallback(
    async (block: PromptBlock) => {
      if (!confirm(`"${block.nameKo}" 이미지를 삭제하시겠습니까?`)) return
      const res = await updateBlock(
        { imageUrl: null },
        { pathOverride: `/api/admin/prompt-blocks/${block.id}` },
      )
      if (res) {
        addToast({ type: 'success', message: '이미지가 삭제되었습니다.' })
        refetch()
      }
    },
    [updateBlock, addToast, refetch],
  )

  const handleSeed = useCallback(async () => {
    if (
      !confirm('시드 데이터를 실행하시겠습니까? 기존 builtin 블록의 promptText가 업데이트됩니다.')
    )
      return
    const res = await seedBlocks(undefined)
    if (res) {
      addToast({ type: 'success', message: '시드 데이터 적용 완료' })
      refetch()
    }
  }, [seedBlocks, addToast, refetch])

  const handleClearAllImages = useCallback(async () => {
    const count = blocks.filter((b) => b.imageUrl).length
    if (count === 0) {
      addToast({ type: 'info', message: '삭제할 이미지가 없습니다.' })
      return
    }
    if (!confirm(`모든 블록의 이미지(${count}개)를 삭제하시겠습니까?`)) return
    const res = await clearAllImages(undefined)
    if (res) {
      addToast({ type: 'success', message: `${count}개 블록의 이미지가 삭제되었습니다.` })
      refetch()
    }
  }, [blocks, clearAllImages, addToast, refetch])

  // 전체 블록에서 사용중인 모든 태그 집합 (필터 칩 노출용)
  const allTags = useMemo(() => {
    const set = new Set<string>()
    for (const b of blocks) for (const t of b.tags ?? []) set.add(t)
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ko'))
  }, [blocks])

  // 선택된 필터 태그가 있으면 OR 매칭 — 하나라도 포함하는 블록만 표시
  const visibleBlocks = useMemo(() => {
    if (filterTags.length === 0) return blocks
    const filterSet = new Set(filterTags)
    return blocks.filter((b) => (b.tags ?? []).some((t) => filterSet.has(t)))
  }, [blocks, filterTags])

  const toggleFilterTag = (tag: string) => {
    setFilterTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    )
  }

  if (isLoading) {
    return (
      <div className={styles.root}>
        <Spinner />
      </div>
    )
  }

  // 카드/리스트 공용 아이콘 액션 렌더러
  const renderBlockActions = (block: PromptBlock) => (
    <>
      <Tooltip
        content={
          generatingBlockIds.has(block.id)
            ? '생성 중...'
            : block.imageUrl
              ? '이미지 재생성'
              : '이미지 생성'
        }
        position="top"
      >
        <button
          className={styles.iconBtn}
          onClick={() => openGenerateModal(block)}
          disabled={generatingBlockIds.has(block.id)}
        >
          {generatingBlockIds.has(block.id) ? <Spinner /> : <ImagePlus size={16} />}
        </button>
      </Tooltip>
      {block.imageUrl && (
        <Tooltip content="이미지 삭제" position="top">
          <button className={styles.iconBtn} onClick={() => handleCardDeleteImage(block)}>
            <ImageOff size={16} />
          </button>
        </Tooltip>
      )}
      <Tooltip content="수정" position="top">
        <button className={styles.iconBtn} onClick={() => openEditModal(block)}>
          <Pencil size={16} />
        </button>
      </Tooltip>
      {!block.isBuiltin && (
        <Tooltip content="삭제" position="top">
          <button
            className={cn(styles.iconBtn, styles.iconBtnDanger)}
            onClick={() => handleDelete(block)}
          >
            <Trash2 size={16} />
          </button>
        </Tooltip>
      )}
    </>
  )

  // 태그 입력 필드 (생성/수정 모달 공용) — 콤마는 onChange split, Enter/Backspace 는 keydown
  //
  // 콤마를 keydown 에서 잡으면 한글 IME 합성 중인 마지막 음절이 commit 되기 전에
  // tagDraft 를 읽어 누락된 채 처리되고, 직후 IME 가 그 음절을 빈 input 에 다시
  // 떨어뜨려 "마지막 글자만 남는" 현상이 발생한다.
  // → 콤마는 그냥 입력되도록 두고, onChange 에서 split 해 마지막 조각만 input 에 남긴다.
  const handleTagDraftChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value.includes(',')) {
      const parts = value.split(',')
      const completed = parts.slice(0, -1)
      const remainder = parts[parts.length - 1] ?? ''
      setFormTags((prev) => normalizeTags([...prev, ...completed]))
      setTagDraft(remainder)
    } else {
      setTagDraft(value)
    }
  }

  const handleTagDraftKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // IME 합성 중인 Enter 는 합성 종료 신호이므로 무시 (한글 마지막 음절 누락 방지)
    if (e.nativeEvent.isComposing) return
    if (e.key === 'Enter') {
      e.preventDefault()
      const raw = e.currentTarget.value
      if (raw.trim()) {
        setFormTags((prev) => normalizeTags([...prev, raw]))
        setTagDraft('')
      }
    } else if (e.key === 'Backspace' && tagDraft === '' && formTags.length > 0) {
      // 빈 입력에서 백스페이스 → 마지막 칩 제거
      setFormTags((prev) => prev.slice(0, -1))
    }
  }

  const handleTagDraftBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const raw = e.currentTarget.value
    if (raw.trim()) {
      setFormTags((prev) => normalizeTags([...prev, raw]))
      setTagDraft('')
    }
  }

  const removeTag = (tag: string) => {
    setFormTags((prev) => prev.filter((t) => t !== tag))
  }

  const renderTagsField = () => (
    <div className={styles.tagsField}>
      <label className={styles.tagsFieldLabel}>태그</label>
      <div
        className={styles.tagInputWrap}
        onClick={(e) => {
          // wrap 영역 클릭 시 input 포커스
          const input = (e.currentTarget.querySelector('input') as HTMLInputElement | null)
          input?.focus()
        }}
      >
        {formTags.map((tag) => (
          <span key={tag} className={styles.tagChip}>
            {tag}
            <button
              type="button"
              className={styles.tagChipRemove}
              onClick={() => removeTag(tag)}
              aria-label={`${tag} 태그 제거`}
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          type="text"
          className={styles.tagInput}
          value={tagDraft}
          onChange={handleTagDraftChange}
          onKeyDown={handleTagDraftKeyDown}
          onBlur={handleTagDraftBlur}
          placeholder={formTags.length === 0 ? '태그 입력 후 Enter (콤마로도 추가)' : ''}
          maxLength={TAG_MAX_LEN}
        />
      </div>
      <span className={styles.tagsHint}>
        분류·검색용 자유 입력 (어드민 전용 · 최대 {TAG_MAX_COUNT}개)
      </span>
    </div>
  )

  // 이미지 필드 렌더러 (생성/수정 모달 공용)
  const renderImageField = () => (
    <div className={styles.imageField}>
      <label className={styles.imageFieldLabel}>대표 이미지</label>
      {formImageUrl ? (
        <>
          <div className={styles.imagePreview}>
            <img
              src={formImageUrl}
              alt="미리보기"
              className={styles.imagePreviewImg}
              onClick={() => setLightboxUrl(formImageUrl)}
              style={{ cursor: 'pointer' }}
            />
            <Button size="sm" variant="ghost" onClick={() => setFormImageUrl(null)}>
              이미지 삭제
            </Button>
          </div>
          {generatedCandidates.length > 1 && (
            <div className={styles.candidateGrid}>
              {generatedCandidates.map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt={`후보 ${i + 1}`}
                  className={cn(
                    styles.candidateThumb,
                    url === formImageUrl && styles.candidateThumbSelected,
                  )}
                  onClick={() => setFormImageUrl(url)}
                />
              ))}
            </div>
          )}
          <div className={styles.imageActions}>
            <select
              className={styles.modelSelect}
              value={imageModelId}
              onChange={(e) => setImageModelId(e.target.value)}
            >
              <option value="" disabled>
                {imageModelOptions.length === 0 ? '없음' : '선택하세요'}
              </option>
              {imageModelOptions.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <Button
              size="sm"
              variant="secondary"
              onClick={handleGenerateImage}
              disabled={imageGenerating || !formPromptText || !imageModelId}
            >
              {imageGenerating ? '생성 중...' : '다시 생성'}
            </Button>
            <label className={styles.imageUploadBtn}>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
                disabled={imageUploading}
              />
              {imageUploading ? '업로드 중...' : '직접 업로드'}
            </label>
          </div>
        </>
      ) : imageGenerating ? (
        <div className={styles.imageGeneratingPlaceholder}>
          <Spinner />
          <span className={styles.imageGeneratingText}>AI 이미지 생성 중...</span>
        </div>
      ) : (
        <div className={styles.imageUploadArea}>
          <label className={styles.imageUploadBtn}>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
              disabled={imageUploading}
            />
            {imageUploading ? '업로드 중...' : '이미지 업로드'}
          </label>
          <select
            className={styles.modelSelect}
            value={imageModelId}
            onChange={(e) => setImageModelId(e.target.value)}
          >
            <option value="" disabled>
              {imageModelOptions.length === 0 ? '없음' : '선택하세요'}
            </option>
            {imageModelOptions.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            variant="secondary"
            onClick={handleGenerateImage}
            disabled={imageGenerating || !formPromptText || !imageModelId}
          >
            {imageGenerating ? '생성 중...' : 'AI 이미지 생성'}
          </Button>
        </div>
      )}
    </div>
  )

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h2 className={styles.headerTitle}>프롬프트 블록 관리</h2>
        <div className={styles.headerActions}>
          <Button size="sm" variant="ghost" onClick={handleClearAllImages} disabled={clearing}>
            {clearing ? '삭제 중...' : '이미지 전체 삭제'}
          </Button>
          <Button size="sm" variant="secondary" onClick={handleSeed} disabled={seeding}>
            {seeding ? '시딩 중...' : '시드 데이터'}
          </Button>
          <Button size="sm" variant="primary" onClick={openCreateModal}>
            블록 추가
          </Button>
        </div>
      </div>

      <div className={styles.refImageBar}>
        <ImageIcon size={16} className={styles.refImageIcon} />
        <span className={styles.refImageLabel}>참조 이미지</span>
        <div className={styles.refImageSlot}>
          <span className={styles.refImageRole}>신랑</span>
          {refGroomUrl ? (
            <>
              <img
                src={refGroomUrl}
                alt="신랑 참조"
                className={styles.refImageThumb}
                onClick={() => setLightboxUrl(refGroomUrl)}
              />
              <button
                className={styles.refImageRemove}
                onClick={() => {
                  setRefGroomUrl(null)
                  saveReferenceImages(null, refBrideUrl)
                }}
                title="신랑 참조 이미지 삭제"
              >
                <X size={14} />
              </button>
            </>
          ) : (
            <label className={styles.refImageUploadBtn}>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => handleReferenceUpload('groom', e)}
                style={{ display: 'none' }}
                disabled={referenceUploading === 'groom'}
              />
              {referenceUploading === 'groom' ? '업로드 중...' : '선택'}
            </label>
          )}
        </div>
        <div className={styles.refImageSlot}>
          <span className={styles.refImageRole}>신부</span>
          {refBrideUrl ? (
            <>
              <img
                src={refBrideUrl}
                alt="신부 참조"
                className={styles.refImageThumb}
                onClick={() => setLightboxUrl(refBrideUrl)}
              />
              <button
                className={styles.refImageRemove}
                onClick={() => {
                  setRefBrideUrl(null)
                  saveReferenceImages(refGroomUrl, null)
                }}
                title="신부 참조 이미지 삭제"
              >
                <X size={14} />
              </button>
            </>
          ) : (
            <label className={styles.refImageUploadBtn}>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => handleReferenceUpload('bride', e)}
                style={{ display: 'none' }}
                disabled={referenceUploading === 'bride'}
              />
              {referenceUploading === 'bride' ? '업로드 중...' : '선택'}
            </label>
          )}
        </div>
      </div>

      <section className={styles.typeSection}>
        <div className={styles.typeSectionHeader}>
          <h3 className={styles.typeSectionTitle}>컨셉 블록</h3>
          <div className={styles.sectionRight}>
            <span className={styles.blockCount}>
              {filterTags.length > 0
                ? `${visibleBlocks.length} / ${blocks.length}개`
                : `${blocks.length}개`}
            </span>
            <div className={styles.viewToggle}>
              <button
                className={cn(styles.viewBtn, viewMode === 'grid' && styles.viewBtnActive)}
                onClick={() => setViewMode('grid')}
                title="그리드"
              >
                <LayoutGrid size={16} />
              </button>
              <button
                className={cn(styles.viewBtn, viewMode === 'list' && styles.viewBtnActive)}
                onClick={() => setViewMode('list')}
                title="리스트"
              >
                <List size={16} />
              </button>
            </div>
          </div>
        </div>
        {/* 필터 바 — 등록된 태그가 있을 때만 노출 */}
        {allTags.length > 0 && (
          <div className={styles.filterBar}>
            <span className={styles.filterBarLabel}>태그 필터</span>
            {allTags.map((tag) => {
              const active = filterTags.includes(tag)
              return (
                <button
                  key={tag}
                  type="button"
                  className={cn(styles.filterChip, active && styles.filterChipActive)}
                  onClick={() => toggleFilterTag(tag)}
                  aria-pressed={active}
                >
                  {tag}
                </button>
              )
            })}
            {filterTags.length > 0 && (
              <button
                type="button"
                className={styles.filterReset}
                onClick={() => setFilterTags([])}
              >
                전체 보기
              </button>
            )}
          </div>
        )}

        {blocks.length === 0 ? (
          <p className={styles.emptyText}>등록된 블록이 없습니다.</p>
        ) : visibleBlocks.length === 0 ? (
          <p className={styles.emptyText}>선택한 태그에 해당하는 블록이 없습니다.</p>
        ) : viewMode === 'list' ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={visibleBlocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
              <div className={styles.listView}>
                {visibleBlocks.map((block, index) => (
                  <SortableItemWrap
                    key={block.id}
                    id={block.id}
                    index={index}
                    isGrid={false}
                    className={cn(styles.listRow, !block.isActive && styles.inactive)}
                  >
                    {({ dragHandle, badge }) => (
                      <>
                        {dragHandle}
                        {badge}
                        <div
                          className={styles.listThumb}
                          onClick={() =>
                            block.imageUrl &&
                            !failedImages.has(block.imageUrl) &&
                            !generatingBlockIds.has(block.id) &&
                            setLightboxUrl(block.imageUrl)
                          }
                        >
                          {generatingBlockIds.has(block.id) ? (
                            <Spinner />
                          ) : (block.thumbnailUrl || block.imageUrl) &&
                            !failedImages.has(block.thumbnailUrl || block.imageUrl || '') ? (
                            <img
                              src={block.thumbnailUrl || block.imageUrl!}
                              alt={block.nameKo}
                              onError={() =>
                                setFailedImages((prev) =>
                                  new Set(prev).add(block.thumbnailUrl || block.imageUrl!),
                                )
                              }
                            />
                          ) : (
                            <span className={styles.listThumbEmpty}>
                              {block.imageUrl ? '준비중' : '🖼'}
                            </span>
                          )}
                        </div>
                        <div className={styles.listInfo}>
                          <div className={styles.listNameRow}>
                            <span className={styles.listName}>{block.nameKo}</span>
                            {block.isBuiltin && <Badge color="blue">Builtin</Badge>}
                            {!block.isActive && <Badge color="gray">비활성</Badge>}
                            {block.popularityRank !== null && (
                              <Badge color="green">
                                #{block.popularityRank} · 최근 7일 {block.weeklyUsageCount}회
                              </Badge>
                            )}
                            {block.totalUsageCount > 0 && (
                              <Badge color="gray">누적 {block.totalUsageCount}회</Badge>
                            )}
                          </div>
                          <span className={styles.listSlug}>{block.slug}</span>
                          {block.tags && block.tags.length > 0 && (
                            <div className={styles.tagsRow}>
                              {block.tags.slice(0, 4).map((t) => (
                                <span key={t} className={styles.tagsRowChip}>{t}</span>
                              ))}
                              {block.tags.length > 4 && (
                                <span className={styles.tagsRowMore}>+{block.tags.length - 4}</span>
                              )}
                            </div>
                          )}
                        </div>
                        <button
                          className={cn(styles.toggle, block.isActive && styles.toggleOn)}
                          onClick={() => handleToggleActive(block)}
                        />
                        <div className={styles.divider} />
                        <div className={styles.listActions}>{renderBlockActions(block)}</div>
                      </>
                    )}
                  </SortableItemWrap>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={visibleBlocks.map((b) => b.id)} strategy={rectSortingStrategy}>
              <div className={styles.gridView}>
                {visibleBlocks.map((block, index) => (
                  <SortableItemWrap
                    key={block.id}
                    id={block.id}
                    index={index}
                    isGrid={true}
                    className={cn(styles.gridCard, !block.isActive && styles.inactive)}
                  >
                    {({ dragHandle, badge }) => (
                      <>
                        <div className={styles.gridCardImgWrap}>
                          {badge}
                          {dragHandle}
                          {(block.thumbnailUrl || block.imageUrl) &&
                          !failedImages.has(block.thumbnailUrl || block.imageUrl || '') ? (
                            <img
                              className={styles.gridCardImg}
                              src={block.thumbnailUrl || block.imageUrl!}
                              alt={block.nameKo}
                              onClick={() =>
                                !generatingBlockIds.has(block.id) && setLightboxUrl(block.imageUrl)
                              }
                              style={{ cursor: generatingBlockIds.has(block.id) ? 'default' : 'pointer' }}
                              onError={() =>
                                setFailedImages((prev) =>
                                  new Set(prev).add(block.thumbnailUrl || block.imageUrl!),
                                )
                              }
                            />
                          ) : (
                            <div className={styles.gridCardEmptyImg}>
                              {block.imageUrl ? '준비중입니다' : '🖼'}
                            </div>
                          )}
                          {generatingBlockIds.has(block.id) && (
                            <div className={styles.generatingOverlay}>
                              <Spinner />
                              <span className={styles.generatingOverlayText}>생성 중...</span>
                            </div>
                          )}
                        </div>
                        <div className={styles.gridCardBody}>
                          <div className={styles.gridCardTop}>
                            <span className={styles.gridCardName}>{block.nameKo}</span>
                            {block.isBuiltin && <Badge color="blue">Builtin</Badge>}
                            {block.popularityRank !== null && (
                              <Badge color="green">
                                #{block.popularityRank} · 최근 7일 {block.weeklyUsageCount}회
                              </Badge>
                            )}
                            {block.totalUsageCount > 0 && (
                              <Badge color="gray">누적 {block.totalUsageCount}회</Badge>
                            )}
                          </div>
                          <span className={styles.listSlug}>{block.slug}</span>
                          {block.tags && block.tags.length > 0 && (
                            <div className={styles.tagsRow}>
                              {block.tags.slice(0, 3).map((t) => (
                                <span key={t} className={styles.tagsRowChip}>{t}</span>
                              ))}
                              {block.tags.length > 3 && (
                                <span className={styles.tagsRowMore}>+{block.tags.length - 3}</span>
                              )}
                            </div>
                          )}
                          <div className={styles.gridCardBottom}>
                            <button
                              className={cn(styles.toggle, block.isActive && styles.toggleOn)}
                              onClick={() => handleToggleActive(block)}
                            />
                            <div className={styles.listActions}>{renderBlockActions(block)}</div>
                          </div>
                        </div>
                      </>
                    )}
                  </SortableItemWrap>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </section>

      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="프롬프트 블록 추가"
      >
        <div className={styles.modalContent}>
          <Input
            label="Slug"
            value={formSlug}
            onChange={(e: any) => setFormSlug(e.target.value)}
            placeholder="예: SUNSET_BEACH"
          />
          <div className={styles.modalRow}>
            <Input
              label="이름 (한글)"
              value={formNameKo}
              onChange={(e: any) => setFormNameKo(e.target.value)}
              placeholder="예: 선셋 비치"
            />
            <Input
              label="이름 (영문)"
              value={formNameEn}
              onChange={(e: any) => setFormNameEn(e.target.value)}
              placeholder="예: Sunset Beach"
            />
          </div>
          <Textarea
            label="프롬프트 텍스트"
            value={formPromptText}
            onChange={(e: any) => setFormPromptText(e.target.value)}
            placeholder="프롬프트 텍스트를 입력하세요"
            rows={5}
          />
          {renderImageField()}
          <Input
            label="정렬 순서"
            type="number"
            value={String(formSortOrder)}
            onChange={(e: any) => setFormSortOrder(Number(e.target.value))}
          />
          {renderTagsField()}
          <div className={styles.modalActions}>
            <Button variant="ghost" onClick={() => setShowCreateModal(false)}>
              취소
            </Button>
            <Button variant="primary" onClick={handleCreate} disabled={creating}>
              {creating ? '생성 중...' : '생성'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!editBlock}
        onClose={() => setEditBlock(null)}
        title={`블록 수정: ${editBlock?.nameKo ?? ''}`}
      >
        {editBlock && (
          <div className={styles.modalContent}>
            <Input label="Slug" value={editBlock.slug} disabled />
            <div className={styles.modalRow}>
              <Input
                label="이름 (한글)"
                value={formNameKo}
                onChange={(e: any) => setFormNameKo(e.target.value)}
              />
              <Input
                label="이름 (영문)"
                value={formNameEn}
                onChange={(e: any) => setFormNameEn(e.target.value)}
              />
            </div>
            <Textarea
              label="프롬프트 텍스트"
              value={formPromptText}
              onChange={(e: any) => setFormPromptText(e.target.value)}
              rows={5}
            />
            {renderImageField()}
            <Input
              label="정렬 순서"
              type="number"
              value={String(formSortOrder)}
              onChange={(e: any) => setFormSortOrder(Number(e.target.value))}
            />
            {renderTagsField()}
            <div className={styles.sparklineSection}>
              <label className={styles.sparklineLabel}>주간 사용량 추이</label>
              <BlockTrendSparkline blockId={editBlock.id} />
            </div>
            <div className={styles.modalActions}>
              <Button variant="ghost" onClick={() => setEditBlock(null)}>
                취소
              </Button>
              <Button variant="primary" onClick={handleUpdate} disabled={updating}>
                {updating ? '수정 중...' : '수정'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={!!generateTarget}
        onClose={() => setGenerateTarget(null)}
        title={`이미지 생성: ${generateTarget?.nameKo ?? ''}`}
      >
        {generateTarget && (
          <div className={styles.generateModal}>
            <div className={styles.generateModalField}>
              <label className={styles.imageFieldLabel}>모델 선택</label>
              <select
                className={styles.modelSelect}
                value={cardModelId}
                onChange={(e) => setCardModelId(e.target.value)}
              >
                <option value="" disabled>
                  {imageModelOptions.length === 0 ? '사용 가능한 모델 없음' : '모델을 선택하세요'}
                </option>
                {imageModelOptions.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.modalActions}>
              <Button variant="ghost" onClick={() => setGenerateTarget(null)}>
                취소
              </Button>
              <Button variant="primary" onClick={handleCardGenerate} disabled={!cardModelId}>
                생성
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {lightboxUrl && (
        <div className={styles.lightboxOverlay} onClick={() => setLightboxUrl(null)}>
          <img
            src={lightboxUrl}
            alt="크게보기"
            className={styles.lightboxImage}
            onClick={(e) => e.stopPropagation()}
          />
          <button className={styles.lightboxClose} onClick={() => setLightboxUrl(null)}>
            &times;
          </button>
        </div>
      )}
    </div>
  )
}
